import { ArrowLeft, Check, Trash2 } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  RECORDING_WARNING,
  SENSITIVE_PHOTO_WARNING
} from "../copy/disclaimers";
import { DESCRIPTION_MAX_LENGTH, DRAFT_DESCRIPTION_MAX_LENGTH } from "../services/app/constants";
import { hasActiveVoiceJob } from "../services/app/reportStatus";
import { getMediaSaveErrorCopy, sanitizePlainText } from "../services/app/validation";
import {
  trackAiSuggestionApplied,
  trackAiSuggestionAvailable,
  trackAudioRecordStarted,
  trackAudioRecordStopped,
  trackAudioSaveFailed,
  trackAudioSaveSuccess,
  trackConditionDeleted,
  trackMicPermissionDenied,
  trackNoteApproved,
  trackNoteTyped
} from "../services/analytics/analyticsEvents";
import {
  acceptPendingAiText,
  approveIssue,
  deleteIssue,
  discardPendingAiText,
  saveIssueAudio,
  saveIssueText
} from "../services/db/reportRepo";
import type { Issue, Room, TranscriptionQueueJob } from "../services/db/schema";
import { useNetworkStatus } from "../services/sync/networkStatus";
import { notifyTranscriptionQueue } from "../services/sync/transcriptionQueue";
import { RecordingList } from "./RecordingList";
import { AudioRecorder } from "./AudioRecorder";
import { BlobImage } from "./BlobImage";
import { StatusBadge } from "./StatusBadge";

interface IssueEditorProps {
  issue: Issue;
  room: Room;
  transcriptionJob?: TranscriptionQueueJob;
  recordings: TranscriptionQueueJob[];
  voiceRecordingDisabledReason?: string;
  onBack: () => void;
  onChanged: () => void;
  onDeleted: () => void;
}

type SaveState = "idle" | "saving" | "saved" | "error";

export function IssueEditor({
  issue,
  room,
  transcriptionJob,
  recordings,
  voiceRecordingDisabledReason,
  onBack,
  onChanged,
  onDeleted
}: IssueEditorProps): JSX.Element {
  const online = useNetworkStatus();
  const [description, setDescription] = useState(issue.description ?? "");
  const [message, setMessage] = useState<string>();
  const [busy, setBusy] = useState(false);
  const [saveState, setSaveState] = useState<SaveState>(issue.description ? "saved" : "idle");
  const [recordingActive, setRecordingActive] = useState(false);
  const candidate = issue.pendingSuggestedDescription ?? issue.pendingAiDescription ?? "";
  const [reviewDraft, setReviewDraft] = useState({ source: candidate, text: candidate });
  const candidateDraft = reviewDraft.text;
  const candidateStale = reviewDraft.source !== candidate;
  useEffect(() => {
    // An arriving transcript or typed-note autosave must not replace an edited review draft.
    setReviewDraft((current) => current.text === current.source
      ? { source: candidate, text: candidate }
      : current);
  }, [candidate]);
  const descriptionRef = useRef(issue.description ?? "");
  const lastPersistedDescriptionRef = useRef(issue.description ?? "");
  const issueIdRef = useRef(issue.id);
  const pendingAiTrackedRef = useRef<string>();
  const saveQueueRef = useRef<Promise<void>>(Promise.resolve());
  const autosaveTimerRef = useRef<number | undefined>(undefined);

  useEffect(() => {
    const incomingDescription = issue.description ?? "";
    const switchedIssue = issueIdRef.current !== issue.id;
    const hasLocalEdits = descriptionRef.current !== lastPersistedDescriptionRef.current;

    if (switchedIssue || !hasLocalEdits || incomingDescription === descriptionRef.current) {
      issueIdRef.current = issue.id;
      descriptionRef.current = incomingDescription;
      lastPersistedDescriptionRef.current = incomingDescription;
      setDescription(incomingDescription);
      setSaveState(incomingDescription ? "saved" : "idle");
      setMessage(undefined);
    }
  }, [issue.id, issue.description]);

  useEffect(() => {
    if (description === lastPersistedDescriptionRef.current) {
      return undefined;
    }

    autosaveTimerRef.current = window.setTimeout(() => {
      void persistDescription(description, { skipIfStale: true });
    }, 650);

    return () => clearAutosaveTimer();
  }, [description]);

  useEffect(() => () => {
    clearAutosaveTimer();
    // A tab switch or phone Back can unmount before the debounce fires.
    const value = descriptionRef.current;
    if (value !== lastPersistedDescriptionRef.current) {
      void persistDescription(value, { skipIfStale: false });
    }
  }, []);

  useEffect(() => {
    if ((issue.pendingAiDescription || issue.pendingSuggestedDescription) && pendingAiTrackedRef.current !== issue.id) {
      pendingAiTrackedRef.current = issue.id;
      trackAiSuggestionAvailable();
    }
  }, [issue.id, issue.pendingAiDescription, issue.pendingSuggestedDescription]);

  const capturedAt = useMemo(
    () =>
      new Intl.DateTimeFormat(undefined, {
        dateStyle: "medium",
        timeStyle: "short"
      }).format(new Date(issue.capturedAt)),
    [issue.capturedAt]
  );

  const hasNote = description.trim().length > 0;
  const voiceWorkPending = hasActiveVoiceJob(issue) || Boolean(issue.audioBlobKey || transcriptionJob);
  const isNoteTooLong = description.length > DESCRIPTION_MAX_LENGTH;
  const draftCharactersRemaining = DRAFT_DESCRIPTION_MAX_LENGTH - description.length;
  const hasUnsavedChanges = description !== lastPersistedDescriptionRef.current;
  const saveStatusLabel = getSaveStatusLabel(saveState, hasUnsavedChanges, hasNote);
  const noteStatusLabel = getNoteStatusLabel(description.length, saveStatusLabel);
  const canFinish = voiceWorkPending || Boolean(candidate) || ((issue.transcriptionStatus === "approved" || hasNote) && !isNoteTooLong);
  const voiceStatusMessage = getVoiceStatusMessage(transcriptionJob, online);
  const recorderDisabledReason = busy ? "Saving changes..." : voiceRecordingDisabledReason;

  function clearAutosaveTimer(): void {
    if (autosaveTimerRef.current) {
      window.clearTimeout(autosaveTimerRef.current);
      autosaveTimerRef.current = undefined;
    }
  }

  function updateDescription(value: string): void {
    const nextValue = value.slice(0, DRAFT_DESCRIPTION_MAX_LENGTH);
    if (value.length > DRAFT_DESCRIPTION_MAX_LENGTH) {
      setMessage(`Voice and typed notes are limited to ${DRAFT_DESCRIPTION_MAX_LENGTH} draft characters on this device.`);
    }
    descriptionRef.current = nextValue;
    setDescription(nextValue);
    if (nextValue !== lastPersistedDescriptionRef.current) {
      setSaveState("idle");
    }
  }

  async function persistDescription(
    nextDescription: string,
    options: { skipIfStale: boolean }
  ): Promise<boolean> {
    const saveTask = async () => {
      if (options.skipIfStale && nextDescription !== descriptionRef.current) {
        return;
      }
      if (nextDescription === lastPersistedDescriptionRef.current) {
        return;
      }

      setSaveState("saving");
      await saveIssueText(issue.id, "", nextDescription);
      lastPersistedDescriptionRef.current = nextDescription;
      if (nextDescription.trim()) {
        trackNoteTyped(nextDescription.length);
      }
      if (nextDescription === descriptionRef.current) {
        setSaveState(nextDescription.trim() ? "saved" : "idle");
      }
      onChanged();
    };

    const queuedSave = saveQueueRef.current.then(saveTask, saveTask);
    saveQueueRef.current = queuedSave.catch(() => {});

    try {
      await queuedSave;
      return true;
    } catch (error) {
      setSaveState("error");
      setMessage(error instanceof Error ? error.message : "Could not save this note.");
      return false;
    }
  }

  async function leaveEditor() {
    setBusy(true);
    clearAutosaveTimer();
    try {
      const saved = await persistDescription(descriptionRef.current, { skipIfStale: false });
      if (saved) {
        onBack();
      }
    } finally {
      setBusy(false);
    }
  }

  async function finishIssue() {
    if (voiceWorkPending || candidate) {
      setBusy(true);
      clearAutosaveTimer();
      try {
        const saved = await persistDescription(descriptionRef.current, { skipIfStale: false });
        if (saved) {
          onBack();
        }
      } finally {
        setBusy(false);
      }
      return;
    }

    if (!canFinish) {
      setMessage(
        isNoteTooLong
          ? `Shorten this note to ${DESCRIPTION_MAX_LENGTH} characters before marking it ready.`
          : "Add a note before marking this condition note ready."
      );
      return;
    }

    setBusy(true);
    clearAutosaveTimer();
    try {
      if (issue.transcriptionStatus !== "approved" || hasUnsavedChanges) {
        const saved = await persistDescription(descriptionRef.current, { skipIfStale: false });
        if (!saved) {
          return;
        }
        await approveIssue(issue.id, "", descriptionRef.current, false);
        trackNoteApproved(descriptionRef.current.length);
        navigator.vibrate?.(10);
        onChanged();
      }
      onBack();
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Could not finish this condition note.");
    } finally {
      setBusy(false);
    }
  }

  async function removeIssue() {
    if (!window.confirm("Delete this condition note from the local draft?")) {
      return;
    }
    setBusy(true);
    clearAutosaveTimer();
    try {
      await deleteIssue(issue.id);
      trackConditionDeleted();
      onDeleted();
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Could not delete this condition note.");
    } finally {
      setBusy(false);
    }
  }

  async function handleRecorded(blob: Blob, mimeType: string, durationMs: number, recordingId: string) {
    setBusy(true);
    try {
      await saveIssueAudio(issue.id, blob, mimeType, durationMs, recordingId);
      trackAudioSaveSuccess({ durationMs, recordingBytes: blob.size, mimeType });
      setMessage(
        online
          ? "Recording saved. Add another recording or tap Done to keep going."
          : "Recording saved on this device. You can keep recording offline. Transcription resumes when this app is open and online."
      );
      onChanged();
      notifyTranscriptionQueue();
    } catch (error) {
      trackAudioSaveFailed(error);
      setMessage(getMediaSaveErrorCopy(error, "recording"));
      throw error;
    } finally {
      setBusy(false);
    }
  }

  async function resolveSpokenNotes(accept: boolean): Promise<void> {
    if (candidateStale || recordingActive) return;
    setBusy(true);
    clearAutosaveTimer();
    try {
      const saved = await persistDescription(descriptionRef.current, { skipIfStale: false });
      if (!saved) return;
      if (accept) {
        await acceptPendingAiText(issue.id, candidateDraft, reviewDraft.source);
        const next = sanitizePlainText(candidateDraft, DRAFT_DESCRIPTION_MAX_LENGTH);
        trackAiSuggestionApplied(next.length);
        descriptionRef.current = next;
        lastPersistedDescriptionRef.current = next;
        setDescription(next);
        setSaveState(next ? "saved" : "idle");
      } else {
        await discardPendingAiText(issue.id, reviewDraft.source);
      }
      setReviewDraft({ source: "", text: "" });
      onChanged();
      // Accepting the saved transcript completes this visit; the note stays editable.
      if (accept) onBack();
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Could not save your choice. Your recordings are still saved.");
      onChanged();
    } finally { setBusy(false); }
  }

  return (
    <section className="screen">
      <div className="topbar">
        <button
          className="icon-button"
          type="button"
          aria-label="Back to room"
          disabled={busy || recordingActive}
          onClick={() => void leaveEditor()}
        >
          <ArrowLeft size={20} aria-hidden="true" />
        </button>
        <div className="topbar__title">
          <h1>{room.name}</h1>
          <p>{capturedAt}</p>
        </div>
        <StatusBadge status={issue.transcriptionStatus} />
      </div>

      <div className="editor-layout">
        <div>
          <div className="photo-frame">
            <BlobImage blobKey={issue.photoBlobKey} alt={`Condition note photo in ${room.name}`} loading="eager" />
          </div>
          <p className="trust-line">{SENSITIVE_PHOTO_WARNING}</p>
        </div>

        <div className="editor-panel">
          {candidate && (
            <div className="notice notice--warning">
              <strong>Spoken notes ready to review</strong>
              <p>Your recordings are combined in the order you made them. Edit the draft below before using it.</p>
              <label htmlFor="spoken-draft">Combined draft</label>
              <textarea id="spoken-draft" className="textarea" value={candidateDraft} disabled={busy || recordingActive} onChange={(event) => setReviewDraft((current) => ({ ...current, text: event.target.value }))} />
              <p className="helper">{candidateDraft.length} / {DRAFT_DESCRIPTION_MAX_LENGTH} draft characters. Each recording's transcript stays saved.</p>
              {candidateStale && (
                <div role="status">
                  <p>The saved notes changed while you were editing. Your edits are still shown above. Copy any edits you want to keep, then reload the combined draft to review the latest notes.</p>
                  <button className="button" type="button" disabled={busy || recordingActive} onClick={() => setReviewDraft({ source: candidate, text: candidate })}>Reload combined draft</button>
                </div>
              )}
              <div className="button-row">
                <button className="button button--primary" type="button" disabled={busy || recordingActive || candidateStale || candidateDraft.length > DRAFT_DESCRIPTION_MAX_LENGTH} onClick={() => void resolveSpokenNotes(true)}>Use spoken notes</button>
                <button className="button" type="button" disabled={busy || recordingActive || candidateStale} onClick={() => void resolveSpokenNotes(false)}>Keep my typed note</button>
              </div>
            </div>
          )}

          <AudioRecorder
            onRecorded={handleRecorded}
            idleLabel={recordings.length ? "Record another note" : "Record note"}
            onActivityChange={setRecordingActive}
            onRecordStarted={trackAudioRecordStarted}
            onRecordStopped={trackAudioRecordStopped}
            onPermissionDenied={trackMicPermissionDenied}
            disabledReason={recorderDisabledReason}
          />
          <p className="trust-line">{RECORDING_WARNING}</p>
          <p className="trust-line">
            Every recording is saved with this photo. Audio stays on this device until you mark the note ready or remove the recording.
          </p>
          {voiceStatusMessage && <div className="notice">{voiceStatusMessage}</div>}
          <RecordingList recordings={recordings} onChanged={onChanged} disabled={busy || recordingActive} />

          <div className="field field--note">
            <div className="field__label-row">
              <label htmlFor="issue-description">Note</label>
              <span
                className={`autosave autosave--${isNoteTooLong ? "error" : saveState}`}
                id="issue-save-status"
                role="status"
                aria-live="polite"
              >
                {noteStatusLabel}
              </span>
            </div>
            <textarea
              id="issue-description"
              className="textarea"
              value={description}
              aria-describedby="issue-save-status"
              placeholder="Describe the condition shown in the photo."
              onChange={(event) => updateDescription(event.target.value)}
              onBlur={() => {
                clearAutosaveTimer();
                void persistDescription(descriptionRef.current, { skipIfStale: false });
              }}
            />
            {draftCharactersRemaining <= 0 && (
              <p className="helper helper--warning">
                Draft limit reached. Shorten this note before adding more text.
              </p>
            )}
          </div>

          {issue.transcriptionError && <div className="notice notice--warning">{issue.transcriptionError}</div>}
          {message && (
            <p className="helper" role="status" aria-live="polite">
              {message}
            </p>
          )}

          <div className="editor-actions">
            <button
              className="button button--primary editor-actions__primary"
              type="button"
              disabled={busy || recordingActive || !canFinish}
              onClick={() => void finishIssue()}
            >
              <Check size={18} aria-hidden="true" />
              Done
            </button>
            <button className="button button--danger" type="button" disabled={busy || recordingActive} onClick={() => void removeIssue()}>
              <Trash2 size={18} aria-hidden="true" />
              Delete condition note
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}

function getSaveStatusLabel(saveState: SaveState, hasUnsavedChanges: boolean, hasNote: boolean): string {
  if (saveState === "saving") {
    return "Saving...";
  }
  if (saveState === "error") {
    return "Save failed";
  }
  if (hasUnsavedChanges) {
    return "Saving soon";
  }
  return hasNote ? "Saved" : "Photo saved";
}

function getNoteStatusLabel(length: number, saveStatusLabel: string): string {
  if (length > DESCRIPTION_MAX_LENGTH) {
    return `${length} / ${DESCRIPTION_MAX_LENGTH} - shorten by ${length - DESCRIPTION_MAX_LENGTH} characters`;
  }
  return `${saveStatusLabel} - ${length} / ${DESCRIPTION_MAX_LENGTH}`;
}

function getVoiceStatusMessage(job: TranscriptionQueueJob | undefined, online: boolean): string | undefined {
  if (!job) {
    return undefined;
  }
  if (!online && (job.status === "queued" || job.status === "retry_wait")) {
    return "Recording saved on this device. You can keep recording offline. Transcription resumes when this app is open and online.";
  }
  if (job.status === "uploading") {
    if (job.uploadTotalBytes && job.uploadLoadedBytes !== undefined) {
      return `Uploading voice note... ${Math.min(100, Math.round((job.uploadLoadedBytes / job.uploadTotalBytes) * 100))}%`;
    }
    return "Uploading voice note...";
  }
  if (job.status === "transcribing") {
    return "Uploaded. Transcribing voice note... You can keep going.";
  }
  if (job.status === "retry_wait") {
    return `Could not transcribe yet. Retrying ${formatRetryWait(job.nextAttemptAt)}.`;
  }
  if (job.status === "error") {
    return "Could not transcribe. Type a note or retry.";
  }
  return online
    ? "Recording saved. Add another recording or tap Done to keep going."
    : "Recording saved on this device. You can keep recording offline. Transcription resumes when this app is open and online.";
}

function formatRetryWait(nextAttemptAt: string): string {
  const ms = Date.parse(nextAttemptAt) - Date.now();
  if (!Number.isFinite(ms) || ms <= 0) {
    return "soon";
  }
  const seconds = Math.ceil(ms / 1000);
  if (seconds < 60) {
    return `in ${seconds}s`;
  }
  return `in ${Math.ceil(seconds / 60)} min`;
}
